import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useFormikContext } from 'formik';
import { useElements, useStripe, Elements } from '@stripe/react-stripe-js';
import { StripeCardElement, loadStripe } from '@stripe/stripe-js';
import { PaymentMethod } from '@prisma/client';
import { SplitPaymentsProvider } from '../../../context/SplitPaymentsContext';
import FullPageModal from '../../modal/FullPageModal';
import {
  CheckoutData,
  GiftCardType,
  SplitPaymentOnFinishArgs,
} from '../../../types/payments';
import { cleanCents, formatDollarAmount } from '../../../utils/global/helper';
import useSplitPayments from '../../../hooks/payments/useSplitPayments';
import PaymentMethodsList from '../../checkout/PaymentMethodsList';
import useConfirm from '../../../hooks/useConfirm';
import AmountInput from './AmountInput';
import useCreateSplitPaymentsGroup from '../../../hooks/payments/useCreateSplitPaymentsGroup';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
} from '../../../types/appointments';
import Loading from './Loading';
import Error from './Error';
import { invalidateCreateSplitPaymentsGroup } from '../../../services/payments';
import useSplitPaymentsState from '../../../hooks/payments/useSplitPaymentsState';
import useCheckPendingPayment from '../../../hooks/payments/useCheckPendingPayment';
import { STRIPE_PK } from '../../../constants/stripe';
import useStripeOptions from '../../../hooks/stripe/useStripeOptions';
import useInSplitCheckout from '../../../hooks/payments/useInSplitCheckout';
import useGetCustomerGiftCardBalance from '../../../hooks/giftCards/useGetCustomerGiftCardBalance';
import { validateGiftCardAmount } from '../../../helpers/gift-cards';
import { CREDIT_PAYMENT_METHODS } from '../../../constants/payments';
import { isGuestCheckout as checkIsGuestCheckout } from '../../../helpers/payments';
import ColoredAlert from '../../global/ColoredAlert';

const stripePromise = loadStripe(STRIPE_PK as string);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFinish: (args: SplitPaymentOnFinishArgs) => void | Promise<void>;
  checkoutData: CheckoutData;
}

function SplitPaymentFlowComponent({
  isOpen,
  onClose,
  onFinish,
  checkoutData,
}: Props) {
  const confirm = useConfirm();
  const stripe = useStripe();
  const elements = useElements();
  const { values } = useFormikContext<CheckoutFormValues>();

  useInSplitCheckout(isOpen);
  const {
    isComplete,
    isCurrentPaymentComplete,
    isCurrentPaymentCreated,
    isValid,
    paymentNumber,
    remainingAmount,
    maxFaceAmount,
    totalPriceCents,
    effectiveTotalPriceCents,
    validationSchema,
    parsedForm,
    isTerminalPending,
  } = useSplitPaymentsState();
  const {
    form,
    setForm,
    currentIndex,
    splitPaymentsGroup,
    paymentMethods,
    addPaymentMethod,
    reset,
    setSplitPaymentsGroup,
    next,
    isResetting,
    processTerminalPayment,
    isFinalized,
    isFinalizing,
    pendingTerminalPayment,
    onCheckoutCapturable,
    onCheckoutFailed,
  } = useSplitPayments({
    isSocketEnabled: true,
    onFinish,
  });
  useCheckPendingPayment({
    enabled: isTerminalPending,
    splitPayment: {
      pendingTerminalPayment,
      onCheckoutCapturable,
      onCheckoutFailed,
    },
  });
  const { data: giftCardBalanceData } = useGetCustomerGiftCardBalance({
    customer: values.customer,
    enabled: form.paymentMethod === 'GIFT_CARD',
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [splitPaymentsGroupArgs] = useState({
    customer: values.customer,
    guest: checkIsGuestCheckout({
      customer: values.customer,
      appointment: values.appointmentId || null,
    }),
    appointment:
      values.appointmentId && checkoutData.appointment
        ? {
            appointmentId: values.appointmentId,
            appointmentPrice: values.price,
            isNoCharge: checkoutData.appointment.originalPriceCents === 0,
            providerCommissionType: values.providerCommissionType,
            employeeId: values.employeeId || undefined,
            additionalServices: values.additionalServices?.map(s => ({
              serviceId: s.serviceId ?? undefined,
              serviceName: s.serviceName,
              employeeId: s.employeeId,
              providerCommissionType: s.providerCommissionType,
              price: s.price,
            })),
          }
        : null,
    order:
      values.customOrderItems?.length && checkoutData.order
        ? {
            employeeId: values.employeeId || undefined,
            orderItems: values.customOrderItems ?? [],
          }
        : null,
    discountId: checkoutData.discount?.id,
  });

  const { isFetching, isError } = useCreateSplitPaymentsGroup(
    splitPaymentsGroupArgs,
    {
      onSuccess: setSplitPaymentsGroup,
      isEnabled: isOpen,
    },
  );

  const amountCents = cleanCents(parsedForm.amount * 100);
  const giftCardBalanceCents = giftCardBalanceData?.availableBalance ?? 0;

  const isGiftCardError =
    form.paymentMethod === 'GIFT_CARD' &&
    !validateGiftCardAmount({
      giftCardType: form.giftCardType,
      amountCents,
      giftCardBalanceCents,
      totalPriceCents,
      isSplitPayment: true,
    }).isValid;

  const isPreviousCredit =
    currentIndex > 0 &&
    CREDIT_PAYMENT_METHODS.includes(
      paymentMethods[currentIndex - 1]?.paymentMethod,
    );

  const onNext = async () => {
    try {
      if (!isValid || !stripe || !elements || isGiftCardError) return;

      setIsProcessing(true);

      const validated = validationSchema.validateSync(parsedForm);
      const currentPaymentMethod = paymentMethods[currentIndex];
      const isCurrentIncomplete =
        !!currentPaymentMethod && !currentPaymentMethod.isPaymentCollected;
      const isCurrentComplete =
        !!currentPaymentMethod && currentPaymentMethod.isPaymentCollected;

      let cardElement: StripeCardElement | null = null;

      if (validated.paymentMethod === 'MANUAL') {
        const submission = await elements?.submit();
        if (submission?.error) {
          toast.error(submission.error.message);
          return;
        }
        // get setup intent from card element rendered in child
        cardElement = elements?.getElement('card');
        if (!cardElement || !stripe) {
          toast.error('Invalid payment method. Please try again.');
          return;
        }
      }

      const parsedPaymentMethod: PaymentMethod =
        validated.paymentMethod === 'MANUAL'
          ? 'CREDIT'
          : validated.paymentMethod;

      const paymentMethod =
        isCurrentIncomplete || isCurrentComplete
          ? currentPaymentMethod
          : await addPaymentMethod({
              amount: validated.amount,
              paymentMethod: parsedPaymentMethod,
              isManual: validated.paymentMethod === 'MANUAL',
              giftCardType: validated.giftCardType,
            });
      if (!paymentMethod) return;

      const newPaymentMethods = [...paymentMethods];

      if (!isCurrentIncomplete && !isCurrentComplete) {
        newPaymentMethods.push(paymentMethod);
      }

      if (paymentMethod.isPaymentCollected) {
        await next(newPaymentMethods);
      } else if (
        paymentMethod.paymentIntentClientSecret &&
        validated.paymentMethod === 'MANUAL' &&
        cardElement
      ) {
        const result = await stripe.confirmCardPayment(
          paymentMethod.paymentIntentClientSecret,
          {
            payment_method: {
              card: cardElement,
            },
            save_payment_method: false,
            receipt_email: values.customer?.email || undefined,
          },
        );
        if (result.error) {
          toast.error(result.error.message);
          return;
        }

        // Close checkout
        if (
          result.paymentIntent?.status === 'requires_capture' &&
          result.paymentIntent?.payment_method
        ) {
          await next(newPaymentMethods);
        }
      } else if (
        isCurrentIncomplete &&
        paymentMethod.paymentIntentClientSecret &&
        validated.paymentMethod === 'TERMINAL'
      ) {
        const intent = await stripe?.retrievePaymentIntent(
          paymentMethod.paymentIntentClientSecret,
        );
        if (intent?.paymentIntent?.status === 'requires_capture') {
          await next(newPaymentMethods);
          return;
        }

        await processTerminalPayment();
      }
    } catch {
      toast.error('Failed to add payment method');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmExit = async (isClose?: boolean) => {
    if (!paymentMethods.length) return true;
    const title = isClose ? 'Exit Split Checkout' : 'Reset Split Payment';
    const message = isClose
      ? "Are you sure you want to exit this split checkout? If you exit, none of the transactions you've added will be processed."
      : "Are you sure you want to reset this split payment? If you reset, none of the transactions you've added will be processed.";

    const confirmed = await confirm({
      title,
      message,
      isSmall: true,
      positiveText: isClose ? 'Exit' : 'Reset',
      negativeText: 'Go back',
    });

    return confirmed;
  };

  const close = async () => {
    const confirmed = await confirmExit(true);
    if (!confirmed) return;

    await reset(true);

    onClose();
  };

  const onReset = async () => {
    const confirmed = await confirmExit();
    if (!confirmed) return;

    await reset();
    await invalidateCreateSplitPaymentsGroup();
  };

  return (
    <FullPageModal
      modalOpen={isOpen}
      name=""
      setModalOpen={close}
      secondaryButton={
        paymentMethods.length > 0
          ? {
              text: 'Reset',
              onClick: onReset,
              variant: 'light',
              isLoading: isResetting,
              disabled:
                isResetting ||
                isProcessing ||
                isFinalized ||
                isTerminalPending ||
                isFinalizing,
            }
          : undefined
      }
      submitButton={{
        text: isComplete ? 'Charge' : 'Next',
        disabled:
          !isValid ||
          isProcessing ||
          isResetting ||
          isFinalized ||
          isTerminalPending ||
          isFinalizing ||
          isGiftCardError,
        onClick: onNext,
        isLoading: isProcessing || isFinalizing,
      }}>
      {isFetching && <Loading />}
      {isError && !isFetching && <Error />}
      {!isFetching && splitPaymentsGroup && !isError && (
        <Stack maxWidth={600} mx="auto" spacing={4} my={4}>
          {isPreviousCredit && (
            <ColoredAlert
              type="info"
              title="Temporary Authorization Hold"
              subtitle="A temporary authorization hold has been placed on the card used in the previous payment method. Funds have been reserved but not yet captured. This may appear as a pending transaction on the customer's statement and will be automatically released if the payment is not finalized."
              animate={false}
              key={currentIndex}
            />
          )}

          <Typography variant="caption" component="p">
            Payment {paymentNumber}
          </Typography>
          <Typography variant="h1" component="p">
            {formatDollarAmount(parsedForm.amount)}
            <Typography
              variant="inherit"
              component="span"
              color="text.secondary">
              {' '}
              of {formatDollarAmount(remainingAmount)}
            </Typography>
          </Typography>
          <AmountInput
            customer={values.customer}
            paymentMethod={form.paymentMethod}
            value={form.amount}
            onChange={value => setForm({ ...form, amount: value })}
            maxFaceAmount={maxFaceAmount}
            effectiveTotalPriceCents={effectiveTotalPriceCents}
            disabled={isCurrentPaymentCreated || isProcessing || isFinalized}
            isGiftCardError={isGiftCardError}
          />

          <Stack spacing={1}>
            <Typography variant="h3" component="p">
              Payment method
            </Typography>
            <PaymentMethodsList
              isSplitPayment
              splitPaymentPaymentMethods={paymentMethods}
              customer={values.customer}
              giftCardType={{
                value: form.giftCardType,
                set: (value: GiftCardType) => {
                  setForm({ ...form, giftCardType: value });
                },
              }}
              giftCardAmountCents={amountCents}
              totalPriceCents={totalPriceCents}
              disabled={isCurrentPaymentCreated || isProcessing || isFinalized}
              cardElementDisabled={
                isCurrentPaymentComplete || isProcessing || isFinalized
              }
              paymentMethods={[
                'TERMINAL',
                'MANUAL',
                'CASH',
                'DIGITAL',
                'GIFT_CARD',
              ]}
              selectedPaymentMethod={form.paymentMethod}
              onChange={value =>
                setForm({
                  ...form,
                  paymentMethod: value as CheckoutPaymentMethod,
                })
              }
              showFees
            />
          </Stack>
        </Stack>
      )}
    </FullPageModal>
  );
}

function SplitPaymentFlow({ isOpen, onClose, onFinish, checkoutData }: Props) {
  const stripeOptions = useStripeOptions();

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <SplitPaymentsProvider>
        <SplitPaymentFlowComponent
          isOpen={isOpen}
          onClose={onClose}
          onFinish={onFinish}
          checkoutData={checkoutData}
          key={isOpen.toString()}
        />
      </SplitPaymentsProvider>
    </Elements>
  );
}

export default SplitPaymentFlow;
